import React, { useState } from 'react';
import { OverlayPanel } from './OverlayPanel';

interface ShipData {
  shipId: string;
  name?: string;
  ownerId?: string;
  ownerName?: string;
  fleetTag?: string | null;
  titleName?: string | null;
  isNPC?: boolean;
  level?: number;
  hp?: number;
  maxHp?: number;
  faction?: string;
  subfaction?: string;
  defense?: number;
  damage?: number;
}

interface ShipInvestigationPanelProps {
  isOpen: boolean;
  shipData: ShipData | null;
  onClose: () => void;
}

export const ShipInvestigationPanel: React.FC<ShipInvestigationPanelProps> = ({
  isOpen,
  shipData,
  onClose,
}) => {
  const [portraitError, setPortraitError] = useState(false);

  if (!shipData) return null;

  const hpPercent = shipData.maxHp ? (shipData.hp || 0) / shipData.maxHp : 0;
  const hpColor = hpPercent > 0.5 ? '#22c55e' : hpPercent > 0.25 ? '#f59e0b' : '#ef4444';

  const displayName = shipData.ownerName
    ? `${shipData.fleetTag ? `[${shipData.fleetTag}] ` : ''}${shipData.ownerName}${shipData.titleName ? ` - ${shipData.titleName}` : ''}`
    : shipData.name || 'Unknown Ship';

  const factionDisplayName = shipData.subfaction || shipData.faction || 'Unknown';

  // Generate portrait based on ship ID hash
  const portraitIndex = Math.abs(shipData.shipId.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0));
  const portraitSrc = portraitError
    ? '/assets/unknown.png'
    : shipData.isNPC
      ? `/assets/portraits/marines/marine_${(portraitIndex % 5) + 1}.png`
      : `/assets/portraits/pirates/pirate_${(portraitIndex % 16) + 1}.png`;

  return (
    <OverlayPanel
      isOpen={isOpen}
      onClose={onClose}
      closeOnEscape
      closeOnBackdrop
      zIndex={5500}
      width={520}
    >
      <div
        style={{
          backgroundColor: '#0a0e27',
          border: '2px solid #ff6b35',
          fontFamily: '"Courier New", monospace',
          color: '#00ff00',
        }}
      >
        {/* Header with portrait and name */}
        <div
          style={{
            display: 'flex',
            gap: 16,
            padding: 16,
            borderBottom: '1px solid #ff6b35',
            backgroundColor: 'rgba(255, 107, 53, 0.05)',
          }}
        >
          {/* Portrait */}
          <div style={{ flexShrink: 0 }}>
            <img
              src={portraitSrc}
              alt="Ship Captain"
              style={{
                width: 80,
                height: 80,
                objectFit: 'cover',
                border: '2px solid #ff6b35',
                backgroundColor: '#1a1f2e',
              }}
              onError={() => setPortraitError(true)}
            />
          </div>

          {/* Name and faction info */}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            <div
              style={{
                fontSize: 16,
                fontWeight: 'bold',
                color: '#ff6b35',
                marginBottom: 4,
                textTransform: 'uppercase',
                letterSpacing: 1,
              }}
            >
              {displayName}
            </div>
            <div
              style={{
                fontSize: 12,
                color: '#888',
                marginBottom: 8,
              }}
            >
              {factionDisplayName.toUpperCase()}
            </div>
            {shipData.isNPC && (
              <div
                style={{
                  fontSize: 11,
                  color: '#f59e0b',
                  fontWeight: 'bold',
                }}
              >
                [NPC VESSEL]
              </div>
            )}
          </div>
        </div>

        {/* Stats section */}
        <div style={{ padding: 16 }}>
          {/* HP */}
          {shipData.hp !== undefined && shipData.maxHp && (
            <div style={{ marginBottom: 12 }}>
              <div
                style={{
                  fontSize: 11,
                  color: '#888',
                  marginBottom: 4,
                  textTransform: 'uppercase',
                  letterSpacing: 0.5,
                }}
              >
                HP
              </div>
              <div
                style={{
                  display: 'flex',
                  gap: 8,
                  alignItems: 'center',
                }}
              >
                <div
                  style={{
                    flex: 1,
                    height: 12,
                    backgroundColor: '#1a1f2e',
                    border: '1px solid #ff6b35',
                    position: 'relative',
                    overflow: 'hidden',
                  }}
                >
                  <div
                    style={{
                      width: `${hpPercent * 100}%`,
                      height: '100%',
                      backgroundColor: hpColor,
                      transition: 'width 0.3s ease',
                    }}
                  />
                </div>
                <div
                  style={{
                    fontSize: 11,
                    color: '#00ff00',
                    fontWeight: 'bold',
                    minWidth: 60,
                    textAlign: 'right',
                  }}
                >
                  {shipData.hp}/{shipData.maxHp}
                </div>
              </div>
            </div>
          )}

          {/* Defense and Damage in a row */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 12,
              marginBottom: 12,
            }}
          >
            {shipData.defense !== undefined && (
              <div>
                <div
                  style={{
                    fontSize: 11,
                    color: '#888',
                    marginBottom: 4,
                    textTransform: 'uppercase',
                    letterSpacing: 0.5,
                  }}
                >
                  DEF
                </div>
                <div
                  style={{
                    fontSize: 14,
                    color: '#00ff00',
                    fontWeight: 'bold',
                  }}
                >
                  {shipData.defense}
                </div>
              </div>
            )}
            {shipData.damage !== undefined && (
              <div>
                <div
                  style={{
                    fontSize: 11,
                    color: '#888',
                    marginBottom: 4,
                    textTransform: 'uppercase',
                    letterSpacing: 0.5,
                  }}
                >
                  DMG
                </div>
                <div
                  style={{
                    fontSize: 14,
                    color: '#ff6b35',
                    fontWeight: 'bold',
                  }}
                >
                  {shipData.damage}
                </div>
              </div>
            )}
          </div>

          {/* Level */}
          {shipData.level && (
            <div style={{ marginBottom: 12 }}>
              <div
                style={{
                  fontSize: 11,
                  color: '#888',
                  marginBottom: 4,
                  textTransform: 'uppercase',
                  letterSpacing: 0.5,
                }}
              >
                LEVEL
              </div>
              <div
                style={{
                  fontSize: 14,
                  color: '#00ff00',
                  fontWeight: 'bold',
                }}
              >
                {shipData.level}
              </div>
            </div>
          )}

          {/* Ship ID */}
          <div
            style={{
              fontSize: 10,
              color: '#666',
              padding: 8,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              border: '1px solid #333',
              fontFamily: 'monospace',
              wordBreak: 'break-all',
              marginTop: 12,
            }}
          >
            <div
              style={{
                marginBottom: 4,
                color: '#888',
                textTransform: 'uppercase',
                fontSize: 9,
              }}
            >
              SHIP ID
            </div>
            {shipData.shipId}
          </div>
        </div>

        {/* Close Button */}
        <div
          style={{
            padding: 12,
            borderTop: '1px solid #ff6b35',
            backgroundColor: 'rgba(255, 107, 53, 0.05)',
          }}
        >
          <button
            type="button"
            onClick={onClose}
            style={{
              width: '100%',
              padding: '10px 12px',
              backgroundColor: '#1a1f2e',
              border: '1px solid #ff6b35',
              color: '#ff6b35',
              fontSize: 12,
              fontWeight: 600,
              cursor: 'pointer',
              letterSpacing: 1,
              fontFamily: '"Courier New", monospace',
              textTransform: 'uppercase',
              transition: 'all 0.2s ease',
            }}
            onMouseEnter={(e) => {
              const target = e.target as HTMLButtonElement;
              target.style.backgroundColor = '#ff6b35';
              target.style.color = '#0a0e27';
            }}
            onMouseLeave={(e) => {
              const target = e.target as HTMLButtonElement;
              target.style.backgroundColor = '#1a1f2e';
              target.style.color = '#ff6b35';
            }}
          >
            CLOSE
          </button>
        </div>
      </div>
    </OverlayPanel>
  );
};
